import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames'
import { useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'

import hfIcon from 'Assets/images/happyfox.png'
import zendeskIcon from 'Assets/images/zendesk.png'
import fileIcon from 'Assets/images/file.svg?url'
import divider from 'Assets/images/divider.svg?url'

import { addKB } from 'Actions/kb'
import { getAddingState } from 'Selectors/kb'

const getCSVLink = () => {
  const csvColumns = ['Reference', 'Title', 'Content', 'Tags', 'Group']
  const csvFile = new Blob([csvColumns], { type: 'text/csv' })
  const csvLink = window.URL.createObjectURL(csvFile)
  return csvLink
}

const KnowledgeBaseImport = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const isAdding = useSelector(getAddingState)
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm()
  const showCloseButton = location.state?.showCloseButton
  const [kb_type, setKbType] = useState('happyfox_helpdesk')
  const knowledgeBases = [
    {
      kb_type: 'happyfox_helpdesk',
      title: 'HappyFox',
      description: 'Import articles from your HappyFox Knowledge Base',
      placeholder: 'https://acme.happyfox.com',
      icon: hfIcon
    },
    {
      kb_type: 'zendesk_support',
      title: 'Zendesk',
      description: 'Import articles from your Zendesk Knowledge Base',
      placeholder: 'https://acme.zendesk.com',
      icon: zendeskIcon
    },
    {
      kb_type: 'csv_file',
      title: 'File',
      description: 'Import articles from a CSV file following <a href="#csvtemplate">this template</a>',
      placeholder: '',
      icon: fileIcon
    }
  ]

  const onSubmit = (formData) => {
    const { url = '', name, file = [] } = formData
    const origin = url ? new URL(url).origin : ''
    dispatch(addKB({ url: origin, name, kb_type, file: file[0] }))
  }

  const onCloseClick = () => {
    navigate('/knowledge-base')
  }

  const getPlaceholder = (kb_type) => {
    return knowledgeBases
      .filter((kb) => kb.kb_type === kb_type)
      .map((kb) => kb.placeholder)
      .toString()
  }

  const createMarkup = (text) => {
    if (text.includes('#csvtemplate')) {
      text = text.replace('#csvtemplate', getCSVLink())
    }
    return { __html: text }
  }

  return (
    <div className='kb-container'>
      <div className='import-container'>
        {showCloseButton && (
          <div onClick={onCloseClick} className='close-button'>
            &times;
          </div>
        )}
        <div className='import-area'>
          <div className='title'>Import Content From Your Knowledge Base</div>
          <div className='importer-select-container'>
            <div className='icon-list'>
              {knowledgeBases.map((kb) => {
                return (
                  <div
                    key={kb.kb_type}
                    className={classNames('icon-list-item', { active: kb_type === kb.kb_type })}
                    onClick={() => setKbType(kb.kb_type)}
                  >
                    <div className='icon-tile'>
                      <img className='importer-source' src={kb.icon} />
                    </div>
                    <div className='icon-list-text'>
                      <div className='icon-tile-title'>{kb.title}</div>
                      <div className='icon-tile-description' dangerouslySetInnerHTML={createMarkup(kb.description)} />
                    </div>
                  </div>
                )
              })}
            </div>
            <img src={divider} />
            <div className='importer-action-container'>
              <form onSubmit={handleSubmit(onSubmit)}>
                {kb_type !== 'csv_file' && (
                  <fieldset>
                    <label htmlFor='kb_url'>Enter the URL to your Knowledge Base</label>
                    <input
                      id='kb_url'
                      className='text-input'
                      type='text'
                      placeholder={getPlaceholder(kb_type)}
                      {...register('url', {
                        required: 'Please enter a URL',
                        pattern: {
                          value:
                            /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/g,
                          message: 'Please enter a valid URL'
                        }
                      })}
                    />
                    {errors.url && <small className='ml-2 text-danger'>{errors.url.message}</small>}
                  </fieldset>
                )}

                {kb_type === 'csv_file' && (
                  <fieldset>
                    <label htmlFor='csv_file'>Upload a file with your articles</label>
                    <input
                      id='csv_file'
                      className='text-input'
                      type='file'
                      {...register('file', {
                        required: 'Please upload a file',
                        validate: {
                          isCSV: (f) => f[0].type.includes('text/csv') || 'Please upload a csv file'
                        }
                      })}
                    />
                    {errors.file && <small className='ml-2 text-danger'>{errors.file.message}</small>}
                  </fieldset>
                )}

                <fieldset>
                  <label>Give this set of articles a name</label>
                  <input
                    className='text-input'
                    type='text'
                    placeholder="Eg. 'Support Articles'"
                    {...register('name', {
                      required: 'Please enter a name for this knowledge base',
                      minLength: {
                        value: 3,
                        message: 'Please use a longer name'
                      }
                    })}
                  />
                  {errors.name && <small className='ml-2 text-danger'>{errors.name.message}</small>}
                </fieldset>

                <button type='submit' className={classNames('button-primary', { disabled: isAdding })}>
                  Import
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default KnowledgeBaseImport
